import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeClosed, Mail, Lock, ArrowLeft, ArrowRight, CalendarDays,
  ShoppingBag,
  Plane,
  LineChart,
  Box,
  DollarSign,
  Boxes,
 } from "lucide-react";
import { BASE_URL, appConfig } from '../../apiConfig';
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { Link } from "react-router-dom";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(BASE_URL + "/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.status === 200) {
        login(data.data, data.token, data.config);
        navigate("/dashboard");
        setLoading(false);
      } else {
        setError(data.message || t('auth.login_error'));
        setLoading(false);
      }
    } catch (err) {
      setError(t('auth.server_error'));
      setLoading(false);
    }
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-gradient-to-br from-[var(--auth-bg-from)] via-[var(--auth-bg-via)] to-[var(--auth-bg-to)] flex items-center justify-center p-4 pt-0 sm:p-8"
    >
      <div className="w-full max-w-6xl bg-gradient-to-br from-[var(--auth-card-from)] to-[var(--auth-card-to)] rounded-[2rem] shadow-2xl  grid md:grid-cols-2 border border-[var(--color-primary-100)]">
        {/* ===== Form side ===== */}
        <div className="flex flex-col justify-center max-lg:px-2 px-8 sm:px-12 py-8 max-lg:py-6">
          <div className="">
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] px-4 py-1.5 text-xs font-medium text-gray-700">
              {t('auth.my_platform')}
            </span>
          </div>

          <div className="relative w-full h-60 mx-auto mt-[-3rem]">
            <div className="absolute top-0 pt-8 inset-0 m-auto"
            style={{
              WebkitMaskImage: "linear-gradient(to top, transparent 0%, black 5%, black 50%)",
              maskImage: "linear-gradient(to top, transparent 0%, black 5%, black 50%)",
            }}>
              <div className="relative w-72 h-72 mx-auto mb-6">
              {/* gray rings */}
              <div className="absolute inset-0 m-auto w-72 h-72 rounded-full border border-gray-200" />
              <div className="absolute inset-0 m-auto w-56 h-56 rounded-full border border-gray-200" />
              <div className="absolute inset-0 m-auto w-40 h-40 rounded-full border border-gray-200" />
    
              {/* center logo */}
              <div className="absolute inset-0 m-auto w-[6rem] h-[6rem] rounded-full bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-300)] shadow-xl flex items-center justify-center">
                <img src={`/images/${appConfig.slug}-logo-solo.png`} className={`${appConfig.slug == 'simoexpress' ? 'w-16 h-13' : 'w-20 h-20'}`} alt="logo" />
              </div>
    
              {/* orbiting icon: bag - outer ring */}
              <div
                className="absolute inset-0 m-auto w-72 h-72"
                style={{ animation: "orbit-spin 28s linear infinite", animationDelay: "0s" }}
              >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] shadow-sm flex items-center justify-center"
                    style={{ animation: "orbit-spin-reverse 28s linear infinite", animationDelay: "0s" }}
                  >
                    <ShoppingBag className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                  </div>
                </div>
              </div>
    
              {/* orbiting icon: chart - outer ring, opposite side */}
              <div
                className="absolute inset-0 m-auto w-72 h-72"
                style={{ animation: "orbit-spin 28s linear infinite", animationDelay: "-14s" }}
              >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] shadow-sm flex items-center justify-center"
                    style={{ animation: "orbit-spin-reverse 28s linear infinite", animationDelay: "-14s" }}
                  >
                    <LineChart className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                  </div>
                </div>
              </div>
    
              {/* orbiting icon: plane - middle ring */}
              <div
                className="absolute inset-0 m-auto w-56 h-56"
                style={{ animation: "orbit-spin-reverse 22s linear infinite", animationDelay: "-5s" }}
              >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] shadow-sm flex items-center justify-center"
                    style={{ animation: "orbit-spin 22s linear infinite", animationDelay: "-5s" }}
                  >
                    <Plane className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                  </div>
                </div>
              </div>
    
              {/* orbiting icon: box - middle ring, opposite side */}
              <div
                className="absolute inset-0 m-auto w-56 h-56"
                style={{ animation: "orbit-spin-reverse 22s linear infinite", animationDelay: "-16s" }}
              >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] shadow-sm flex items-center justify-center"
                    style={{ animation: "orbit-spin 22s linear infinite", animationDelay: "-16s" }}
                  >
                    <Box className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                  </div>
                </div>
              </div>

              <div
                className="absolute inset-0 m-auto w-40 h-40"
                style={{ animation: "orbit-spin 22s linear infinite", animationDelay: "-16s" }}
              >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] shadow-sm flex items-center justify-center"
                    style={{ animation: "orbit-spin 22s linear infinite", animationDelay: "-16s" }}
                  >
                    <DollarSign className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          <div className="mb-4 px-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
              {t('auth.welcome_back')}
            </h1>
            <p className="text-gray-500 text-xs">{t('auth.login_subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 px-8">
            {error && (
              <div className="text-red-600 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 px-4 py-2.5 rounded-xl text-xs text-center">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-gray-600 mb-2">
                {t('auth.email_label')}
              </label>
              <div className="relative">
                <Mail className={`${isRTL ? "right-4" : "left-4"} absolute top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4`} />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${isRTL ? "pr-11" : "pl-11"} w-full text-xs py-3 rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:text-gray-400`}
                  placeholder={t('auth.email_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-gray-600 mb-2">
                {t('auth.password_label')}
              </label>
              <div className="relative">
                <Lock className={`${isRTL ? "right-4" : "left-4"} absolute top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4`} />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${isRTL ? "pr-11" : "pl-11"} w-full text-xs py-3 rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:text-gray-400`}
                  placeholder={t('auth.password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`${isRTL ? "left-4" : "right-4"} absolute cursor-pointer text-xs top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                  aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                >
                  {showPassword ? <Eye className="w-4 h-4" /> : <EyeClosed className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex gap-1 items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[var(--color-primary-500)] focus:ring-[var(--color-primary-300)]"
                />
                <span className="text-xs text-gray-600">{t('auth.remember_me')}</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-[var(--color-primary-500)] hover:text-[var(--color-primary-400)] font-medium underline underline-offset-2"
              >
                {t('auth.forgot_password')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full text-xs bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] cursor-pointer text-white py-3.5 px-4 rounded-2xl font-semibold flex items-center justify-center group shadow-lg transition-all duration-200 ${
                loading
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)]"
              }`}
            >
              {loading ? (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                  ></path>
                </svg>
              ) : (
                <>
                  {t('auth.login_button')}
                  {
                    isRTL ? (
                      <ArrowLeft className="mr-2 w-4 h-4 group-hover:translate-x-[-4px] transition-transform duration-200" />
                    ) : (
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:-translate-x-[-3px] transition-transform duration-200" />
                    )
                  }
                </>
              )}
            </button>
          </form>

          <div className="mt-4 px-8 flex items-center justify-between text-xs">
            <p className="text-gray-500">
              {t('auth.no_account')}{" "}
              <Link
                to="/register"
                className="text-[var(--color-primary-500)] hover:text-[var(--color-primary-400)] font-semibold"
              >
                {t('auth.create_account_link')}
              </Link>
            </p>
            <Link href="/terms" className="text-gray-400 hover:text-gray-600 underline underline-offset-2">
              {t('auth.terms_and_conditions')}
            </Link>
          </div>
        </div>

        {/* ===== Decorative side - Affiliate Platform ===== */}
        <div className="hidden md:block relative m-3 rounded-[1.6rem] overflow-hidden">
          <img
            src={ appConfig.slug === "nextlead" ?
            "https://images.unsplash.com/photo-1603195827187-459ab02554a0?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              :
            "https://images.unsplash.com/photo-1556565681-306458ef93cd?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          }
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* Gradient overlays */}
          <div className="absolute -top-16 -left-16 w-72 h-72 bg-gradient-to-br from-[var(--color-primary-200)] to-[var(--color-primary-50)] opacity-30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tr from-[var(--color-primary-300)] to-[var(--color-primary-100)] opacity-20 rounded-full blur-3xl" />
          
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/20" />

          {/* Top Stats Card - Today's Earnings */}
          <div className="absolute top-6 right-4 flex gap-4">
            <div className=" bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-sm text-white rounded-2xl px-4 py-3 shadow-xl w-56 border border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">{t('auth.today_earnings')}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)] animate-pulse" />
              </div>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-bold">1,247MAD+</span>
                <span className="text-[10px] text-green-300 flex items-center gap-0.5">
                  ↑ 12.5%
                </span>
              </div>
              <span className="text-[10px] text-white/60">{t('auth.products_sold')}</span>
            </div>
            {/* Weekly Growth Indicator */}
            <div className="bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/10">
              <div className="text-center">
                <span className="text-[10px] text-white/60 block">{t('auth.weekly_growth')}</span>
                <span className="text-lg font-bold text-green-300">24.8%+</span>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <div className="w-1 h-4 bg-green-300/30 rounded-full" />
                  <div className="w-1 h-6 bg-green-300/50 rounded-full" />
                  <div className="w-1 h-8 bg-green-300 rounded-full" />
                  <div className="w-1 h-5 bg-green-300/50 rounded-full" />
                  <div className="w-1 h-3 bg-green-300/30 rounded-full" />
                </div>
              </div>
            </div>

          </div>

          {/* Monthly Revenue Card */}
          <div className="absolute top-36 right-4 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-sm text-white rounded-2xl px-4 py-2 shadow-xl w-44 border border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">{t('auth.total_revenue')}</span>
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                <LineChart className="w-4 h-4 text-white" />
              </div>
            </div>
            <span className="text-lg font-bold">38,000MAD+</span>
          </div>

          {/* Stats Row - Products & Commissions */}
          <div className="absolute bottom-8 left-8 right-8 flex gap-3">
            <div className="flex-1 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-white/60">{t('auth.total_products')}</span>
                  <p className="text-lg font-bold text-white">2,000+</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                  <ShoppingBag className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
            
            <div className="flex-1 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-white/60">{t('auth.commissions')}</span>
                  <p className="text-lg font-bold text-white">8,432MAD</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                  <Boxes className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Floating Stats - Weekly Progress */}
          <div className="absolute top-1/2 left-6 -translate-y-1/2 flex flex-col gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <ShoppingBag className="w-3.5 h-3.5" />
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <LineChart className="w-3.5 h-3.5" />
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <Boxes className="w-3.5 h-3.5" />
            </div>
            
            {/* Progress indicator */}
            <div className="w-0.5 h-12 bg-gradient-to-b from-white/40 to-white/10 mx-auto my-1" />
            <div className="text-[8px] text-white/40 text-center -rotate-90 whitespace-nowrap">
              {t('auth.this_week')}
            </div>
          </div>

          
        </div>
      </div>
    </div>
  );
}